import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getUserByUsername, updateUser } from "../common/api/users";
import { getGroupByName, updateGroup, deleteGroup , getGroups} from "../common/api/groups";
import { useToast } from "./useToast";
import { apiFetch } from "../common/api";

export default function useUserManagementLogic() {
  const [showModal, setShowModal] = useState(true);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedClientCode, setSelectedClientCode] = useState("");
  const [currentView, setCurrentView] = useState("usuarios");
  const [activeModal, setActiveModal] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editGroup, setEditGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminUser, setAdminUser] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const userCache = useRef({});
  const [allUsersCached, setAllUsersCached] = useState([]);
  const [allGroupsCached, setAllGroupsCached] = useState([]);

  const groupCache = useRef({});
   const ITEMS_PER_PAGE = 5;

  const toast = useToast();
  const navigate = useNavigate();
  const SELECTED_CLIENT_CODE_KEY = "selected_client_code";
  const SELECTED_CLIENT_NAME_KEY = "selected_client_name";
  
  const normalizeGroupNames = (groups) => {
    if (!Array.isArray(groups)) return [];

    return groups
      .map((group) => {
        if (typeof group === "string") return group;
        if (group && typeof group === "object") {
          if (typeof group.NOMBRE === "string") return group.NOMBRE;
          if (typeof group.nombre === "string") return group.nombre;
          if (typeof group.name === "string") return group.name;
        }
        return null;
      })
      .filter(
        (groupName) => typeof groupName === "string" && groupName.trim().length > 0
      );
  };

  const normalizeGroupEntries = (groups) => {
    if (!Array.isArray(groups)) return [];

    return groups
      .map((group) => {
        if (!group) return null;

        if (typeof group === "string") {
          const name = group.trim();
          return name.length > 0 ? { name, clientCode: null } : null;
        }

        if (typeof group === "object") {
          const nameCandidate =
            group.NOMBRE ??
            group.nombre ??
            group.name ??
            group.GRUPO ??
            group.grupo ??
            null;

          const name = typeof nameCandidate === "string" ? nameCandidate.trim() : null;

          if (!name) return null;

          const clientRaw =
            group.COD_CLIENTE ??
            group.cod_cliente ??
            group.COD_ID_GROUP ??
            group.cod_id_group ??
            null;

          const clientCode =
            clientRaw !== undefined && clientRaw !== null ? String(clientRaw) : null;

          return { name, clientCode };
        }

        return null;
      })
      .filter((entry) => entry && entry.name.length > 0);
  };

  const normalizeGroupNamesByClient = (groups, clientCode) => {
    const normalized = normalizeGroupEntries(groups);

    if (!clientCode) {
      return Array.from(new Set(normalized.map((group) => group.name).filter(Boolean)));
    }

    const targetCode = String(clientCode);

    return Array.from(
      new Set(
        normalized
          .filter((group) => group.clientCode && String(group.clientCode) === targetCode)
          .map((group) => group.name)
          .filter((name) => typeof name === "string" && name.trim().length > 0)
      )
    );
  };

  const buildGroupMemberCountMap = (users, clientCode) => {
    const map = new Map();

    if (!Array.isArray(users)) {
      return map;
    }

    const targetClientCode = clientCode ? String(clientCode) : "";

    users.forEach((user) => {
      const groupNames = normalizeGroupNamesByClient(user?.GRUPOS, targetClientCode);

      groupNames.forEach((groupName) => {
        if (!groupName) return;
        map.set(groupName, (map.get(groupName) ?? 0) + 1);
      });
    });

    return map;
  };

  const resolveClientCode = (explicitCode) => {
    if (explicitCode !== undefined && explicitCode !== null && explicitCode !== "") {
      return String(explicitCode);
    }

    if (selectedClientCode) {
      return String(selectedClientCode);
    }

    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(SELECTED_CLIENT_CODE_KEY);
      if (stored) {
        return String(stored);
      }
    }

    return "";
  };

  const fetchGroups = async (codClienteParam, usersOverride) => {
  try {
    if (!adminUser) {
      console.error("Admin user non definito");
      return [];
    }

    // 🔹 1. Verifica se ha permessi di gestione
    const hasManagementPermissions = adminUser?.ROLES?.some(
      (role) => role.Rol === "ADMINISTRADOR" || role.Rol === "GESTIONUSERS"
    );

    // 🔹 2. Ottieni i gruppi
    const baseGroups = await getGroups(
      hasManagementPermissions ? "" : adminUser.USER
    );

    if (!Array.isArray(baseGroups)) {
      console.error("Risposta non valida da getGroups:", baseGroups);
      return [];
    }

      const normalizedClientCode = resolveClientCode(codClienteParam);
      const filteredGroups = normalizedClientCode
        ? baseGroups.filter(
            (group) => String(group.COD_CLIENTE) === normalizedClientCode
          )
        : baseGroups;

      // 🔹 3. Ottieni i dettagli completi (incluso ACTIVO)
      const groupsWithDetails = await Promise.all(
        filteredGroups.map(async (group) => {
          try {
            const details = await getGroupByName(group.NOMBRE);
            // Riporta ACTIVO così com'è arrivato dall’API
            const ACTIVO = details?.ACTIVO ?? null;
            return { ...group, ACTIVO };
          } catch (err) {
            console.warn(`Impossibile ottenere stato per gruppo ${group.NOMBRE}`);
            return { ...group, ACTIVO: null };
          }
        })
      );

      // 🔹 4. Calcola memberCount per ogni gruppo
    const referenceUsers = Array.isArray(usersOverride)
      ? usersOverride
      : allUsersCached;

    const memberCountMap = buildGroupMemberCountMap(
      referenceUsers,
      normalizedClientCode
    );

    const groupsWithMembers = groupsWithDetails.map((group) => ({
      ...group,
      memberCount: memberCountMap.get(group.NOMBRE) ?? 0,
    }));

    // 🔹 5. Aggiorna stato hook
    setGroups(groupsWithMembers);
    setAllGroupsCached(groupsWithMembers);
        setCurrentPage((prevPage) => {
      const totalPages = Math.max(
        1,
        Math.ceil(groupsWithMembers.length / ITEMS_PER_PAGE)
      );

      return prevPage > totalPages ? totalPages : prevPage;
    });
    return groupsWithMembers;

  } catch (err) {
    console.error("Errore in fetchGroups:", err);
    toast.error("Errore nel caricamento dei gruppi");
    return [];
  }
};


  // Helper per ottenere user dalla cache
  const getCachedUser = async (username) => {
    if (userCache.current[username]) {
      return userCache.current[username];
    }
    const user = await getUserByUsername(username);
    userCache.current[username] = user;
    return user;
  };

  // Funzione per ottenere gli utenti base di un cliente
  const getUsersByClient = async (codCliente) => {
    const res = await apiFetch(`/users?ID=${codCliente}`);
    if (!res.ok) throw new Error("Non è stato possibile ottenere gli utenti");
    return await res.json();
  };

  // Gestione selezione cliente
  const handleSelectClient = async () => {
    try {
      if (!selectedClient) {
        toast.error("Seleziona un cliente");
        return;
      }

      // Trova il COD_CLIENTE corrispondente
      const client = availableClients.find(c => c.NOMBRE_CLIENTE === selectedClient);
      if (!client) {
        toast.error("Cliente non trovato");
        return;
      }

      const clientCodeString = String(client.COD_CLIENTE);
      setSelectedClientCode(clientCodeString);

      if (typeof window !== "undefined") {
        window.localStorage.setItem(SELECTED_CLIENT_CODE_KEY, clientCodeString);
        window.localStorage.setItem(SELECTED_CLIENT_NAME_KEY, client.NOMBRE_CLIENTE);
      }

      // Svuota le cache
      userCache.current = {};
      groupCache.current = {};
      setCurrentPage(1);

      // Ottieni gli utenti base del cliente
      let basicUsers = await getUsersByClient(client.COD_CLIENTE);

      // Se l’utente ha permessi gestionali ma non ci sono utenti associati,
      // usa se stesso come fallback
      const hasManagementPermissions = adminUser?.ROLES?.some(
        (role) => role.Rol === "ADMINISTRADOR" || role.Rol === "GESTIONUSERS"
      );

      if (!hasManagementPermissions && (!basicUsers || basicUsers.length === 0)) {
        console.warn("Nessun utente trovato per questo cliente, uso adminUser come fallback");
        basicUsers = [
          {
            USER: adminUser.USER,
            COD_ID: adminUser.COD_ID || 0,
            EMAIL: adminUser.EMAIL,
            ACTIVO: "1",
            GRUPOS: adminUser.GRUPOS || [],
          },
        ];
      }


      // Carica i dettagli completi per ogni utente
      const detailedUsers = await Promise.all(
        basicUsers.map(u => getCachedUser(u.USER))
      );

      setAllUsersCached(detailedUsers);
      setUsers(detailedUsers);
      await fetchGroups(clientCodeString, detailedUsers);

    
      setShowModal(false);
      setCurrentView("usuarios");
    } catch (err) {
      console.error(err);
      toast.error("Errore nel caricamento dei dati del cliente");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate("/home");
  };

  const handleAction = async (action, id) => {
    try {
      if (action === "crear-grupo") {
        setActiveModal("newGroup");
      } else if (action === "modificar-usuario") {
        const basicUser = users.find((u) => u.COD_ID === id);
        if (!basicUser) return alert("Usuario no encontrado");
        const user = await getUserByUsername(basicUser.USER);
        if (user) {
          setEditUser(user);
          setActiveModal("editUser");
        }
      } else if (action === "cambiar-password") {
        const basicUser = users.find((u) => u.COD_ID === id);
        if (!basicUser) return alert("Usuario no encontrado");
        const user = await getUserByUsername(basicUser.USER);
        if (user) {
          setEditUser(user);
          setActiveModal("changePassword");
        }
      } else if (action === "permisos-usuario") {
        const basicUser = users.find((u) => u.COD_ID === id);
        if (!basicUser) return alert("Usuario no encontrado");
        /*if (adminUser && basicUser.USER !== adminUser.USER) {
          toast.error("Solo puedes cambiar tu propia contraseña.");
          return;
        }*/
        const user = await getUserByUsername(basicUser.USER);
        if (user) {
          setEditUser(user);
          setActiveModal("permissions");
        }
      } else if (action === "modificar-grupo") {
        const group = groups.find((g) => g.COD_ID === id);
        if (group) {
          setEditGroup(group);
          setActiveModal("editGroup");
        }
      } else if (action === "eliminar-grupo") {
        const group = groups.find((g) => g.COD_ID === id);
        if (!group) {
          toast.error("Grupo no encontrado.");
          return;
        }
        setGroupToDelete(group);
        setActiveModal("deleteGroup");
      }
    } catch (err) {
      alert("Error al ejecutar la acción");
      console.error(err);
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    let isMounted = true;

    try {
        let storedClientCode =
        updatedUser?.COD_ID_GROUP ??
        selectedClientCode ??
        (typeof window !== "undefined"
          ? window.localStorage.getItem(SELECTED_CLIENT_CODE_KEY)
          : "");

      if (
        !storedClientCode &&
        Array.isArray(editUser?.CLIENTES_ASOCIADOS) &&
        editUser.CLIENTES_ASOCIADOS.length > 0
      ) {
        const matchByName = selectedClient
          ? editUser.CLIENTES_ASOCIADOS.find(
              (client) =>
                client?.NOMBRE_CLIENTE &&
                client.NOMBRE_CLIENTE.toLowerCase() ===
                  selectedClient.toLowerCase()
            )
          : null;

        storedClientCode =
          matchByName?.COD_CLIENTE ??
          editUser.CLIENTES_ASOCIADOS[0]?.COD_CLIENTE ??
          storedClientCode;
      }

      const normalizedGroups =
        "GRUPOS" in updatedUser
          ? normalizeGroupNames(updatedUser.GRUPOS)
          : normalizeGroupNames(editUser?.GRUPOS);

      const payload = {
        ...updatedUser,
        ...(storedClientCode
          ? { COD_ID_GROUP: String(storedClientCode) }
          : {}),
        ...(normalizedGroups ? { GRUPOS: normalizedGroups } : {}),
      };
      const result = await updateUser(payload);
      if (!isMounted) return;

      if (result?.status === "Error") {
        return false;
      }

      delete userCache.current[payload.USER];

      let basicUsers = await getUsersByClient(selectedClientCode);
      if (!isMounted) return;
      // Se l’utente ha permessi gestionali ma non ci sono utenti associati,
      // usa se stesso come fallback
      const hasManagementPermissions = adminUser?.ROLES?.some(
        (role) => role.Rol === "ADMINISTRADOR" || role.Rol === "GESTIONUSERS"
      );

      if (!hasManagementPermissions && (!basicUsers || basicUsers.length === 0)) {
        console.warn("Nessun utente trovato per questo cliente, uso adminUser come fallback");
        basicUsers = [
          {
            USER: adminUser.USER,
            COD_ID: adminUser.COD_ID || 0,
            EMAIL: adminUser.EMAIL,
            ACTIVO: "1",
            GRUPOS: adminUser.GRUPOS || [],
          },
        ];
      }
      const details = await Promise.all(basicUsers.map((u) => getCachedUser(u.USER)));
      if (!isMounted) return;

      setUsers(details);
      setAllUsersCached(details);
      await fetchGroups(selectedClientCode, details);
      if (!isMounted) return;
      
      if (activeModal !== "permissions") {
        setEditUser(null);
        setActiveModal(null);
      } else {
        const refreshed = await getCachedUser(payload.USER);
        if (!isMounted) return;
        setEditUser(refreshed);
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      isMounted = false;
    }
  };

  const handleCreateUser = async (newUser) => {
    let isMounted = true;

    try {
      const response = await createUser(newUser);
      if (!isMounted) return false;

      if (!response || response.status === "Error") {
        return false;
      }

      userCache.current = {};

      let basicUsers = await getUsersByClient(selectedClientCode);
      if (!isMounted) return false;
      // Se l’utente ha permessi gestionali ma non ci sono utenti associati,
      // usa se stesso come fallback
      const hasManagementPermissions = adminUser?.ROLES?.some(
        (role) => role.Rol === "ADMINISTRADOR" || role.Rol === "GESTIONUSERS"
      );

      if (!hasManagementPermissions && (!basicUsers || basicUsers.length === 0)) {
        console.warn("Nessun utente trovato per questo cliente, uso adminUser come fallback");
        basicUsers = [
          {
            USER: adminUser.USER,
            COD_ID: adminUser.COD_ID || 0,
            EMAIL: adminUser.EMAIL,
            ACTIVO: "1",
            GRUPOS: adminUser.GRUPOS || [],
          },
        ];
      }
      const detailed = await Promise.all(
        basicUsers.map((u) => getCachedUser(u.USER))
      );
      if (!isMounted) return false;

      setAllUsersCached(detailed);
      setUsers(detailed);
       await fetchGroups(selectedClientCode, detailed);
      if (!isMounted) return;

      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      isMounted = false;
    }
  };

  const handleUpdateGroup = async (updatedGroup) => {
  let isMounted = true;

    try {
      const result = await updateGroup(updatedGroup);
      if (!isMounted) return false;

      if (!result || result.status === "Error") {
        return false;
      }

      const previousName = editGroup?.NOMBRE;
      if (previousName) {
        delete groupCache.current[previousName];
      }
      delete groupCache.current[updatedGroup.NOMBRE];

      userCache.current = {};

      const basicUsers = await getUsersByClient(selectedClientCode);
      if (!isMounted) return false;

      const detailedUsers = await Promise.all(
        basicUsers.map((u) => getCachedUser(u.USER))
      );
      if (!isMounted) return false;

      await fetchGroups(selectedClientCode, detailedUsers);
      if (!isMounted) return false;

      setAllUsersCached(detailedUsers);
      setUsers(detailedUsers);
      setEditGroup(null);
      setActiveModal(null);

      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      isMounted = false;
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) {
      toast.error("Grupo no seleccionado.");
      return false;
    }

    //let isMounted = true;
    const group = groupToDelete;

    try {
      await deleteGroup({
        COD_ID: group.COD_ID,
        COD_CLIENTE: group.COD_CLIENTE,
        NOMBRE: group.NOMBRE,
      });
      //if (!isMounted) return false;

      delete groupCache.current[group.NOMBRE];

      /* const updatedGroups = await fetchGroups();
      if (!isMounted) return false;

      const totalItems = updatedGroups?.length ?? 0;
      const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }*/
      setGroupToDelete(null);
      setActiveModal(null);
      toast.success("Grupo eliminado correctamente.");
      const itemsPerPage = 5;
      
      // ✅ Ottieni prima gli utenti del cliente
      const basicUsers = await getUsersByClient(selectedClientCode);
      const detailedUsers = await Promise.all(
        basicUsers.map((u) => getCachedUser(u.USER))
      );
      
      // ✅ Passa gli utenti a fetchGroups
      fetchGroups(selectedClientCode, detailedUsers)
        .then((updatedGroups) => {
          const totalItems = updatedGroups?.length ?? 0;
          const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

          setCurrentPage((prevPage) =>
            prevPage > totalPages ? totalPages : prevPage
          );
        })
        .catch((error) => {
          console.error("Error al recargar grupos:", error);
        });

      return true;
    } catch (err) {
      console.error("Error al eliminar grupo:", err);
      toast.error("No se pudo eliminar el grupo.");
      return false;
    } finally {
      isMounted = false;
    }
  };

  const groupsWithMemberCount = useMemo(() => {
    const normalizedClientCode = resolveClientCode();
    const memberCountMap = buildGroupMemberCountMap(
      allUsersCached,
      normalizedClientCode
    );

    return groups.map((group) => ({
      ...group,
      memberCount: memberCountMap.get(group.NOMBRE) ?? 0,
    }));
  }, [groups, allUsersCached, selectedClientCode]);

  // Caricamento iniziale: ottieni i clienti associati all'admin
  useEffect(() => {
    let isMounted = true;

    async function fetchAdminData() {
      try {
        const currentUsername = localStorage.getItem("current_user");
        if (!currentUsername) {
          toast.error("Usuario no identificado");
          navigate("/login");
          return;
        }

        const admin = await getCachedUser(currentUsername);
        if (!isMounted) return;

        setAdminUser(admin);

        if (admin.CLIENTES_ASOCIADOS && admin.CLIENTES_ASOCIADOS.length > 0) {
          setAvailableClients(admin.CLIENTES_ASOCIADOS);
          // ✅ Seleziona automaticamente il primo cliente
                    let initialClient = admin.CLIENTES_ASOCIADOS[0];

          if (typeof window !== "undefined") {
            const storedCode = window.localStorage.getItem(SELECTED_CLIENT_CODE_KEY);
            if (storedCode) {
              const matchingClient = admin.CLIENTES_ASOCIADOS.find(
                (client) => String(client.COD_CLIENTE) === String(storedCode)
              );
              if (matchingClient) {
                initialClient = matchingClient;
              }
            }
          }

          setSelectedClient(initialClient.NOMBRE_CLIENTE);
          setSelectedClientCode(String(initialClient.COD_CLIENTE));

        } else {
          toast.error("No tienes clientes asociados");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error al cargar datos del administrador");
      }
    }

    fetchAdminData();

    return () => {
      isMounted = false;
    };
  }, [navigate, toast]);

  return {
    showModal,
    setShowModal,
    selectedClient,
    setSelectedClient,
    availableClients,
    currentView,
    setCurrentView,
    activeModal,
    setActiveModal,
    users,
    setUsers,
    groups,
    setGroups,
    editUser,
    setEditUser,
    editGroup,
    setEditGroup,
    groupToDelete,
    setGroupToDelete,
    handleSelectClient,
    handleCloseModal,
    handleAction,
    handleCreateUser,
    handleUpdateUser,
    handleUpdateGroup,
    handleDeleteGroup,
    groupsWithMemberCount,
    currentPage,
    setCurrentPage,
    allUsersCached,
    setAllUsersCached,
    allGroupsCached,
    setAllGroupsCached,
    adminUser,
    selectedClientCode,
  };
}